import React, { useState, useEffect } from 'react';
import { Mail, Phone, Search, Eye, User, Copy, FileText } from 'lucide-react';
import { apiService, ContactRequest, ContactRequestStatus } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import Modal from '../components/Modal';

const ContactRequests: React.FC = () => {
  const { showToast } = useToast();
  const [requests, setRequests] = useState<ContactRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal state
  const [selectedRequest, setSelectedRequest] = useState<ContactRequest | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const data = await apiService.getContactRequests();
      setRequests(data || []);
    } catch (error) {
      console.error('Failed to fetch contact requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: ContactRequestStatus) => {
    try {
      await apiService.updateContactRequest(id, { status });
      setRequests(requests.map(req =>
        req.id === id ? { ...req, status } : req
      ));
      showToast('Status updated successfully!', 'success');

      // Update modal data if open
      if (selectedRequest && selectedRequest.id === id) {
        setSelectedRequest({ ...selectedRequest, status });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      showToast('Error updating status.', 'error');
    }
  };

  const handleViewDetails = (request: ContactRequest) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-[10px] font-bold uppercase">New</span>;
      case 'in_progress':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-[10px] font-bold uppercase">Pending</span>;
      case 'resolved':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-[10px] font-bold uppercase">Resolved</span>;
      case 'closed':
        return <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-[10px] font-bold uppercase">Closed</span>;
      default:
        return <span className="px-2 py-1 bg-gray-50 text-gray-400 rounded text-[10px] font-bold uppercase">{status}</span>;
    }
  };

  const filteredRequests = requests.filter(req => {
    const matchesSearch = req.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (req.subject && req.subject.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Contact Requests</h1>
          <p className="text-gray-600">Review and manage messages from the website contact form</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500/20"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-500/20"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="in_progress">Pending</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-50">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">User</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Message</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Type</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Status</th>
              <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filteredRequests.map((request) => (
              <tr key={request.id} className="hover:bg-gray-50/50 transition">
                <td className="px-6 py-4">
                  <div className="text-sm font-bold text-gray-900">{request.name}</div>
                  <div className="text-xs text-gray-400 flex items-center mt-1">
                    <Mail className="h-3 w-3 mr-1" /> {request.email}
                  </div>
                  {request.phone && (
                    <div className="text-[10px] text-gray-400 flex items-center mt-0.5 font-medium">
                      <Phone className="h-2.5 w-2.5 mr-1" /> {request.phone}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 max-w-sm">
                  <div className="text-sm font-medium text-gray-800 line-clamp-1">{request.subject || 'N/A'}</div>
                  <div className="text-xs text-gray-500 mt-1 italic line-clamp-2">{request.message}</div>
                </td>
                <td className="px-6 py-4">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[9px] font-bold uppercase">
                    {(request.request_type || 'General').replace('_', ' ')}
                  </span>
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(request.status)}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleViewDetails(request)}
                      className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <select
                      value={request.status}
                      onChange={(e) => updateStatus(request.id, e.target.value as ContactRequestStatus)}
                      className="border border-gray-100 rounded px-2 py-1.5 text-[10px] font-bold uppercase outline-none bg-white focus:border-orange-200"
                    >
                      <option value="new">New</option>
                      <option value="in_progress">Pending</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Close</option>
                    </select>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredRequests.length === 0 && (
          <div className="p-12 text-center text-gray-400 text-sm italic">No requests found matching filters.</div>
        )}
      </div>

      {/* Details Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Request Details"
        maxWidth="max-w-4xl"
      >
        {selectedRequest && (
          <div className="space-y-8">
            {/* Header / Status Section */}
            <div className="flex flex-col sm:flex-row items-start justify-between gap-6">
              <div className="space-y-3 flex-1">
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-orange-600 uppercase tracking-[0.2em] leading-none mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
                    Message Subject
                  </p>
                  <h3 className="text-4xl font-black text-gray-900 leading-tight tracking-tight">{selectedRequest.subject || 'N/A'}</h3>
                </div>

                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedRequest.status)}
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-lg text-[10px] font-bold uppercase border border-gray-200 shadow-sm">
                    {(selectedRequest.request_type || 'General').replace('_', ' ')}
                  </span>
                </div>
              </div>

              <div className="text-left sm:text-right bg-blue-50/80 backdrop-blur-sm p-5 rounded-2xl border border-blue-100/50 shadow-sm min-w-[200px] flex flex-col justify-center">
                <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest mb-2">Received On</p>
                <p className="text-2xl font-black text-gray-900 leading-none">{new Date(selectedRequest.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                <p className="text-xs text-blue-600 font-bold mt-1.5">{new Date(selectedRequest.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            </div>

            {/* Sender Info Section */}
            <div className="p-8 bg-gray-50/50 rounded-[2.5rem] border border-gray-100 space-y-6 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-10 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity pointer-events-none">
                <Mail className="w-32 h-32 text-gray-900" />
              </div>

              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2">
                <User className="w-3 h-3" />
                Sender Information
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12 relative z-10">
                <div className="space-y-1.5">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Sender Name</p>
                  <p className="text-xl font-black text-gray-900">{selectedRequest.name}</p>
                </div>

                <div className="space-y-1.5 group/item">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Contact Email</p>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(selectedRequest.email);
                        showToast('Email copied!', 'success');
                      }}
                      className="opacity-0 group-hover/item:opacity-100 p-1 text-gray-400 hover:text-orange-600 transition-all rounded"
                      title="Copy Email"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                  <a href={`mailto:${selectedRequest.email}`} className="text-lg font-bold text-orange-600 hover:text-orange-700 transition-colors flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center">
                      <Mail className="w-4 h-4 text-orange-500" />
                    </div>
                    {selectedRequest.email}
                  </a>
                </div>

                <div className="space-y-1.5 group/item">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Phone Number</p>
                    <button
                      onClick={() => {
                        if (selectedRequest.phone) {
                          navigator.clipboard.writeText(selectedRequest.phone);
                          showToast('Phone number copied!', 'success');
                        }
                      }}
                      className="opacity-0 group-hover/item:opacity-100 p-1 text-gray-400 hover:text-orange-600 transition-all rounded"
                      title="Copy Phone"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                  <a href={`tel:${selectedRequest.phone}`} className="text-2xl font-black text-gray-900 hover:text-orange-600 transition-colors flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center">
                      <Phone className="w-4 h-4 text-orange-500" />
                    </div>
                    {selectedRequest.phone || 'N/A'}
                  </a>
                </div>
              </div>
            </div>

            {/* Message Content Section */}
            <div className="space-y-4">
              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2 px-2">
                <FileText className="w-3 h-3" />
                Message Content
              </h4>
              <div className="p-10 bg-white border border-gray-100 rounded-[2.5rem] text-gray-700 leading-relaxed shadow-[0_20px_50px_rgba(0,0,0,0.04)] relative">
                <div className="absolute top-6 left-6 text-gray-100 opacity-50">
                  <svg className="w-12 h-12 fill-current" viewBox="0 0 24 24">
                    <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H16.017C14.9124 8 14.017 7.10457 14.017 6V5C14.017 3.89543 14.9124 3 16.017 3H19.017C21.2261 3 23.017 4.79086 23.017 7V15C23.017 18.3137 20.3307 21 17.017 21H14.017ZM1.017 21L1.017 18C1.017 16.8954 1.91243 16 3.017 16H6.017C6.56928 16 7.017 15.5523 7.017 15V9C7.017 8.44772 6.56928 8 6.017 8H3.017C1.91243 8 1.017 7.10457 1.017 6V5C1.017 3.89543 1.91243 3 3.017 3H6.017C8.22614 3 10.017 4.79086 10.017 7V15C10.017 18.3137 7.33071 21 4.017 21H1.017Z" />
                  </svg>
                </div>
                <div className="absolute bottom-6 right-6 text-gray-100 opacity-50 rotate-180">
                  <svg className="w-12 h-12 fill-current" viewBox="0 0 24 24">
                    <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H16.017C14.9124 8 14.017 7.10457 14.017 6V5C14.017 3.89543 14.9124 3 16.017 3H19.017C21.2261 3 23.017 4.79086 23.017 7V15C23.017 18.3137 20.3307 21 17.017 21H14.017ZM1.017 21L1.017 18C1.017 16.8954 1.91243 16 3.017 16H6.017C6.56928 16 7.017 15.5523 7.017 15V9C7.017 8.44772 6.56928 8 6.017 8H3.017C1.91243 8 1.017 7.10457 1.017 6V5C1.017 3.89543 1.91243 3 3.017 3H6.017C8.22614 3 10.017 4.79086 10.017 7V15C10.017 18.3137 7.33071 21 4.017 21H1.017Z" />
                  </svg>
                </div>
                <div className="relative z-10 font-medium italic text-gray-600 text-center text-xl px-4">
                  {selectedRequest.message}
                </div>
              </div>
            </div>

            {/* Actions Section */}
            <div className="pt-6 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
              <span className="text-sm text-gray-500 font-bold uppercase tracking-wider">Update Status:</span>
              <div className="flex gap-2 flex-wrap justify-center">
                {(['new', 'in_progress', 'resolved', 'closed'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => updateStatus(selectedRequest.id, status)}
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 ${selectedRequest.status === status
                      ? 'bg-gray-900 text-white shadow-lg shadow-gray-200 -translate-y-0.5'
                      : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300'
                      }`}
                  >
                    {status.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal >
    </div >
  );
};

export default ContactRequests;